// ============================================================================
// HAI Reporting Tool - Real-Time Metrics Hook
// Fetches live metrics from Firestore for dashboard display
// ============================================================================

import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/services/firebase';
import type { PatientRecord, HCAISurveillanceCase } from '@/types';
import { aggregateHCAISurveillanceCases } from '@/utils/calculations';

export interface RealTimeMetrics {
  dataEntryComplete: number;
  pendingEntries: number;
  activeAlerts: number;
  totalPatients: number;
  atRiskPatients: number;
  weeklyHAICases: number;
  wards: number;
}

/**
 * Hook to fetch real-time metrics for the dashboard
 * Queries data from the past 7 days
 */
export function useRealTimeMetrics() {
  const [metrics, setMetrics] = useState<RealTimeMetrics>({
    dataEntryComplete: 0,
    pendingEntries: 0,
    activeAlerts: 0,
    totalPatients: 0,
    atRiskPatients: 0,
    weeklyHAICases: 0,
    wards: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMetrics();

    // Refresh metrics every 30 seconds for near real-time updates
    const interval = setInterval(loadMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadMetrics = async () => {
    setLoading(true);
    setError(null);

    try {
      // Calculate date range (past 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];
      const todayStr = new Date().toISOString().split('T')[0];

      console.log('[useRealTimeMetrics] Fetching metrics for date range:', sevenDaysAgoStr, 'to', todayStr);

      // Fetch patient records from past 7 days
      const patientQuery = query(
        collection(db, 'patientRecords'),
        where('recordDate', '>=', sevenDaysAgoStr),
        where('recordDate', '<=', todayStr)
      ) as any;

      const patientDocs = await getDocs(patientQuery);
      const patientRecords = patientDocs.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as any)
      })) as PatientRecord[];

      // Get unique wards from patient records
      const uniqueWards = new Set(patientRecords.map(p => p.wardName));
      const wardCount = uniqueWards.size;

      // Count total HAI cases across all patients (from patient records)
      let totalHAICases = 0;
      patientRecords.forEach(patient => {
        if (patient.haiCases && Array.isArray(patient.haiCases)) {
          totalHAICases += patient.haiCases.length;
        }
      });

      // Also count HCAI surveillance cases (comprehensive case forms)
      // Note: createdAt is a Firestore Timestamp, so we need to convert our date
      const sevenDaysAgoTimestamp = new Date(sevenDaysAgoStr);

      console.log('[useRealTimeMetrics] Querying hcaiCases since:', sevenDaysAgoTimestamp);

      const hcaiCaseQuery = query(
        collection(db, 'hcaiCases'),
        where('createdAt', '>=', sevenDaysAgoTimestamp)
      ) as any;

      try {
        const hcaiCaseDocs = await getDocs(hcaiCaseQuery);
        const hcaiCases = hcaiCaseDocs.docs.map(doc => {
          const rawData = doc.data() as any;
          return {
            id: doc.id,
            ...rawData,
            createdAt: rawData.createdAt?.toDate?.() || new Date(),
            updatedAt: rawData.updatedAt?.toDate?.() || new Date()
          } as HCAISurveillanceCase;
        });

        const aggregatedSurveillance = aggregateHCAISurveillanceCases(hcaiCases);
        const infectionCount = aggregatedSurveillance.uti + aggregatedSurveillance.cabsi + aggregatedSurveillance.vap;

        console.log('[useRealTimeMetrics] Found', infectionCount, 'surveillance infections');
        totalHAICases += infectionCount;
      } catch (hcaiError) {
        console.error('[useRealTimeMetrics] Error fetching HCAI cases:', hcaiError);
        // Continue with just patient record counts
      }

      console.log('[useRealTimeMetrics] Total HAI cases:', totalHAICases);

      // Count unique patients (by patient identifier)
      const uniquePatients = new Set(patientRecords.map(p => p.patientIdentifier)).size;

      // Count at-risk patients (those with devices)
      const patientsWithDevices = patientRecords.filter(p => p.devices && p.devices.length > 0).length;
      const estimatedAtRisk = Math.ceil(patientsWithDevices * 0.12);

      // Count device daily count entries (one per ward per day)
      const deviceCountQuery = query(
        collection(db, 'deviceDailyCounts'),
        where('date', '>=', sevenDaysAgoStr),
        where('date', '<=', todayStr)
      ) as any;

      const deviceCountDocs = await getDocs(deviceCountQuery);
      const dataEntryComplete = deviceCountDocs.docs.length;

      setMetrics({
        dataEntryComplete,
        pendingEntries: 0, // Could calculate based on expected wards
        activeAlerts: 0, // Will be calculated based on alert rules
        totalPatients: uniquePatients,
        atRiskPatients: estimatedAtRisk,
        weeklyHAICases: totalHAICases,
        wards: wardCount
      });
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  };

  return { metrics, loading, error, refetch: loadMetrics };
}
